"use client";
import { useMemo, useEffect, useState } from "react";
import Image from "next/image";
import Skeleton from "react-loading-skeleton";
import styles from "./index.module.css";
import { copyIcon, editIcon, replyIcon, trashCan } from "../../assets";

const parseDate = (dateString) => {
  if (!dateString) return new Date();
  if (typeof dateString === "string" && dateString.includes(" ")) {
    return new Date(dateString.replace(" ", "T") + "Z");
  }
  return new Date(dateString);
};

const ChatBubble = ({
  currentUserId,
  message,
  swipeDistance,
  showDropdown,
  onClickReference,
  bubbleRef,
  isHolding,
  dropdownRef,
  hovered,
  handleOpenMedia,
  handleDelete,
  handleEdit,
  handleCopy,
  handleReply,
  handleRetry,
  closeMenu,
  showSenderName = false,
}) => {
  const { author, media = [], userId, sending, failed } = message;

  const isMine = userId === currentUserId;

  const [mainsLoaded, setMainsLoaded] = useState(
    Array(media?.length || 0).fill(false)
  );

  const [dropUp, setDropUp] = useState(false);

  // ✅ Guard: only compute visible media once, and only if media exists
  const visibleMedia = useMemo(() => {
    if (!Array.isArray(media) || media.length === 0) return [];
    return media.slice(0, 4);
  }, [media]);

  const handleMainLoad = (index) => {
    setMainsLoaded((prev) => {
      const copy = [...prev];
      copy[index] = true;
      return copy;
    });
  };

  const time = parseDate(message.createdAt).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  useEffect(() => {
    if (showDropdown && bubbleRef.current) {
      const rect = bubbleRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      // If not enough space below, flip dropdown upwards
      const spaceBelow = viewportHeight - rect.bottom;
      setDropUp(spaceBelow < 180); // 180px = estimated dropdown height
    }
  }, [showDropdown]);

  const linkify = (text = "") => {
    if (!text) return "";
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const wwwRegex = /(^|[\s])www\.[^\s]+/g;
    const emailRegex = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

    return text
      .replace(urlRegex, (url) => `<a href="${url}" target="_blank">${url}</a>`)
      .replace(wwwRegex, (match) => {
        const prefix = match.startsWith("www.") ? "" : match[0];
        const url = match.trim();
        return `${prefix}<a href="https://${url}" target="_blank">${url}</a>`;
      })
      .replace(emailRegex, (email) => `<a href="mailto:${email}">${email}</a>`)
      .replace(/\n/g, "<br/>");
  };

  const getActions = (isMine, message, handlers) => [
    ...(isMine
      ? [
          {
            label: "Delete",
            icon: trashCan,
            onClick: () => handlers.handleDelete(message),
          },
          {
            label: "Edit",
            icon: editIcon,
            onClick: () => handlers.handleEdit(message),
          },
        ]
      : []),
    {
      label: "Copy",
      icon: copyIcon,
      onClick: () => handlers.handleCopy(message),
    },
    {
      label: "Reply",
      icon: replyIcon,
      onClick: () => handlers.handleReply(message),
    },
  ];

  const actions = getActions(isMine, message, {
    handleDelete,
    handleEdit,
    handleCopy,
    handleReply,
  });

  return (
    <>
      <div
        className={`${styles.messageRow} ${
          isMine ? styles.mine : styles.theirs
        }`}
        style={{
          transform: `translateX(${swipeDistance}px)`,
          transition: swipeDistance === 0 ? "transform 0.25s ease-out" : "none",
        }}
      >
        {isHolding && <span className={styles.holdIndicator}></span>}

        {/* Avatar (only for theirs) */}
        {!isMine && (
          <div className={styles.avatar}>
            {author?.image ? (
              <Image
                src={author.image}
                alt={author?.name || "user"}
                width={32}
                height={32}
                className={styles.avatarImg}
              />
            ) : (
              <div className={styles.avatarFallback}>{author?.name?.[0]}</div>
            )}
          </div>
        )}

        <div
          className={`${styles.messageContent} ${
            isMine ? styles.mine : styles.theirs
          }`}
        >
          {/* 🔹 Author name */}
          {!isMine && showSenderName && author?.name && (
            <div className={styles.senderName}>{author.name}</div>
          )}

          <div
            className={`${styles.bubble} ${
              showDropdown ? styles.highlighted : ""
            }`}
            ref={bubbleRef}
          >
            {/* reference bubble */}
            {message.reference && (
              <div
                className={`${styles.replyBubble} ${
                  isMine ? styles.myBubble : styles.theirBubble
                }`}
                onClick={onClickReference}
              >
                <p className={styles.replyAuthor}>
                  {message.reference.previewAuthor}
                </p>
                {message.reference.previewContent && (
                  <p className={styles.replySnippet}>
                    {message.reference.previewContent}
                  </p>
                )}
                {message.reference.previewImage && (
                  <img
                    src={message.reference.previewImage}
                    alt="Replied"
                    width={24}
                    height={24}
                  />
                )}
              </div>
            )}

            {/* text */}
            {message.comment && (
              <div
                className={styles.text}
                dangerouslySetInnerHTML={{ __html: linkify(message.comment) }}
              />
            )}

            {/* media */}
            {visibleMedia.length > 0 && (
              <div
                className={styles.mediaContainer}
                onClick={(e) => {
                  e.stopPropagation();
                  if (swipeDistance === 0 && visibleMedia.length > 0) {
                    handleOpenMedia(media);
                  }
                }}
              >
                <div
                  className={`${
                    visibleMedia.length === 1
                      ? styles.singleMedia
                      : visibleMedia.length === 3
                      ? styles.gridThree
                      : styles.grid
                  }`}
                >
                  {visibleMedia.map((item, i) => {
                    const isFullWidth = visibleMedia.length === 3 && i === 2;
                    return (
                      <div
                        key={i}
                        className={`${styles.gridItem} ${
                          isFullWidth ? styles.fullWidth : ""
                        }`}
                      >
                        {!mainsLoaded[i] && (
                          <Skeleton
                            width={isFullWidth ? "100%" : 120}
                            height={120}
                          />
                        )}
                        <img
                          src={item.mediaThumb || item.url}
                          alt={item.name}
                          className={`${styles.gridImage} ${
                            visibleMedia.length === 1 ? styles.singleImage : ""
                          }`}
                          style={{
                            opacity: mainsLoaded[i] ? 1 : 0,
                            transition: "opacity 0.3s ease",
                          }}
                          onLoad={() => handleMainLoad(i)}
                        />
                        {i === 3 && media.length > 4 && (
                          <div className={styles.overlay}>
                            +{media.length - 4}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* time + edited */}
            <div className={styles.editRow}>
              <span className={styles.time}>{time}</span>
              {message.commentEdit && (
                <span className={styles.edited}>(edited)</span>
              )}
            </div>

            {/* Hover actions */}
            {hovered && (
              <div
                className={
                  isMine ? styles.actionBubbleMine : styles.actionBubbleOther
                }
              >
                {actions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={action.onClick}
                    className="bubbleIcon"
                    aria-label={`${action.label} message`}
                  >
                    {action.icon}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* dropdown menu */}
      {showDropdown && (
        <div
          className={`${styles.dropdownMenu} ${
            isMine ? styles.dropdownmine : styles.dropdowntheirs
          } ${dropUp ? styles.dropUp : ""}`}
          ref={dropdownRef}
          role="menu"
        >
          {actions.map((action, idx) => (
            <button
              key={idx}
              onClick={() => {
                action.onClick();
                closeMenu();
              }}
              aria-label={`${action.label} message`}
            >
              {action.icon} {action.label}
            </button>
          ))}
        </div>
      )}

      {/* status row for mine */}

      {isMine && (
        <div className={styles.statusRow}>
          {sending && !failed && (
            <span className={styles.sending}>Sending...</span>
          )}
          {failed && (
            <span className={styles.failed}>
              Failed to send
              <button
                disabled={sending}
                onClick={() => handleRetry?.(message)}
                className={styles.retryBtn}
              >
                Retry
              </button>
            </span>
          )}
        </div>
      )}
    </>
  );
};

export default ChatBubble;
